/**
 * DeviceOrientationSource — phone tilt as Input axes.
 *
 * Wraps the W3C `deviceorientation` event and pushes screen-space `lookX`
 * / `lookY` (plus raw `tiltAlpha` / `tiltBeta` / `tiltGamma`) into the
 * shared Input registry under source name `'orientation'`. Game code reads
 * them via `this.sdk.input.axis('lookX')`.
 *
 * Calibration: call `calibrate()` once when the game starts (or when the
 * player taps "recenter") to capture the current orientation as the
 * resting pose. All future readings are reported relative to that zero.
 *
 * iOS 13+ requires a user gesture to grant access. Call
 * `requestPermission()` from a click/tap handler before `install()`.
 */
export class DeviceOrientationSource {
  /**
   * @param {object} opts
   * @param {import('./Input.js').Input} opts.input
   * @param {number} [opts.maxTiltDeg=45] - tilt that maps to the [-1, 1] axis edge
   */
  constructor(opts) {
    this.input = opts.input;
    this.maxTiltDeg = opts.maxTiltDeg || 45;

    this._installed = false;
    this._handler = null;
    this._latest = { alpha: 0, beta: 0, gamma: 0, absolute: false, compass: null };
    this._calibration = { alpha: 0, beta: 0, gamma: 0 };
    this._calibrated = false;
  }

  /**
   * Ask iOS for permission. Must be called from a user gesture handler.
   * Returns 'granted' / 'denied' / 'unsupported'.
   */
  async requestPermission() {
    if (typeof window === 'undefined') return 'unsupported';
    const DOE = window.DeviceOrientationEvent;
    if (!DOE) return 'unsupported';
    if (typeof DOE.requestPermission === 'function') {
      try {
        const result = await DOE.requestPermission();
        return result;
      } catch (_) {
        return 'denied';
      }
    }
    return 'granted';
  }

  /** Start receiving events. No-op outside browsers. */
  install() {
    if (this._installed) return;
    if (typeof window === 'undefined') return;
    this._installed = true;

    this._handler = (ev) => this._onEvent(ev);
    // Prefer absolute orientation when available; falls back to relative.
    window.addEventListener('deviceorientationabsolute', this._handler, true);
    window.addEventListener('deviceorientation', this._handler, true);
  }

  destroy() {
    if (!this._installed) return;
    this._installed = false;
    if (typeof window !== 'undefined') {
      window.removeEventListener('deviceorientationabsolute', this._handler, true);
      window.removeEventListener('deviceorientation', this._handler, true);
    }
    this.input.clearSource('orientation');
  }

  /** Capture the current orientation as the new zero. */
  calibrate() {
    this._calibration.alpha = this._latest.alpha;
    this._calibration.beta = this._latest.beta;
    this._calibration.gamma = this._latest.gamma;
    this._calibrated = true;
    this._publish();
  }

  /** Reset calibration so raw orientation values are reported. */
  resetCalibration() {
    this._calibration = { alpha: 0, beta: 0, gamma: 0 };
    this._calibrated = false;
    this._publish();
  }

  get latest() {
    return Object.assign({}, this._latest);
  }

  // ─────────────────────────────────────────────────────────────── internals

  _onEvent(ev) {
    this._latest.alpha = ev.alpha == null ? this._latest.alpha : ev.alpha;
    this._latest.beta = ev.beta == null ? this._latest.beta : ev.beta;
    this._latest.gamma = ev.gamma == null ? this._latest.gamma : ev.gamma;
    this._latest.absolute = !!ev.absolute;
    // Safari iOS exposes a magnetometer-derived compass heading here.
    if (typeof ev.webkitCompassHeading === 'number') {
      this._latest.compass = ev.webkitCompassHeading;
    } else if (this._latest.absolute) {
      this._latest.compass = this._latest.alpha;
    }
    this._publish();
  }

  _publish() {
    const dAlpha = wrapDeg(this._latest.alpha - this._calibration.alpha);
    const dBeta = this._latest.beta - this._calibration.beta;
    const dGamma = this._latest.gamma - this._calibration.gamma;

    // Map [-maxTilt, +maxTilt] degrees → [-1, +1]. Beta is forward/back tilt
    // (pitch); gamma is left/right tilt (roll).
    const lookX = clamp(dGamma / this.maxTiltDeg, -1, 1);
    const lookY = clamp(dBeta / this.maxTiltDeg, -1, 1);

    this.input.setSourceAxis('orientation', 'lookX', lookX);
    this.input.setSourceAxis('orientation', 'lookY', lookY);
    this.input.setSourceAxis('orientation', 'tiltAlpha', dAlpha);
    this.input.setSourceAxis('orientation', 'tiltBeta', dBeta);
    this.input.setSourceAxis('orientation', 'tiltGamma', dGamma);
  }
}

function clamp(v, lo, hi) {
  if (v < lo) return lo;
  if (v > hi) return hi;
  return v;
}

function wrapDeg(d) {
  // Normalize alpha deltas into (-180, +180].
  let x = d;
  while (x > 180)  x -= 360;
  while (x <= -180) x += 360;
  return x;
}
